import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentAccessToken,
  getCurrentUserId,
  getDealApplicationId,
  getDealBorrowerId,
  getHasPreferredLanguageOverride,
  setDealBorrowerId,
  setPreferredLanguage,
} from "features/authSlice";
import { useGetBorrowerDetailsQuery, useGetBorrowerPersonalInformationQuery, useUpdateUserPreferredLanguageMutation } from "pages/dashboard/home/dashboardService";
import { DEFAULT_LANGUAGE } from "utilities/Constant";
import { extractDealServicePreferredLanguage, normalizeToAppLanguage, toUserDbPreferredLanguage } from "utilities/getPreferredLanguage";

export default function useLoginPreferredLanguageSync() {
  const dispatch = useDispatch();
  const accessToken = useSelector(getCurrentAccessToken);
  const userId = useSelector(getCurrentUserId);
  const dealApplicationId = useSelector(getDealApplicationId);
  const dealBorrowerId = useSelector(getDealBorrowerId);
  const hasPreferredLanguageOverride = useSelector(getHasPreferredLanguageOverride);

  const isAuthenticated = Boolean(accessToken);
  const hasApplicationContext = Boolean(dealApplicationId);

  const {
    data: dealServicePersonalInformation,
    isError: isDealServiceError,
  } = useGetBorrowerPersonalInformationQuery(
    { borrowerId: dealBorrowerId || "" },
    { skip: !isAuthenticated || !hasApplicationContext || !dealBorrowerId },
  );

  const { data: userProfile } = useGetBorrowerDetailsQuery(userId, {
    skip: !isAuthenticated || !userId,
  });
  const [updateUserPreferredLanguage] = useUpdateUserPreferredLanguageMutation();

  useEffect(() => {
    if (!isAuthenticated || !hasApplicationContext) {
      return;
    }

    if (dealServicePersonalInformation?.dealBorrowerId) {
      dispatch(setDealBorrowerId(dealServicePersonalInformation.dealBorrowerId));
    }

    if (hasPreferredLanguageOverride) {
      return;
    }

    if (isDealServiceError) {
      dispatch(setPreferredLanguage(normalizeToAppLanguage(userProfile?.preferredLanguage ?? DEFAULT_LANGUAGE)));
      return;
    }

    const dealServiceLanguage = extractDealServicePreferredLanguage(dealServicePersonalInformation);
    if (!dealServiceLanguage) {
      dispatch(setPreferredLanguage(normalizeToAppLanguage(userProfile?.preferredLanguage ?? DEFAULT_LANGUAGE)));
      return;
    }

    const normalizedDealServiceLanguage = normalizeToAppLanguage(dealServiceLanguage);
    dispatch(setPreferredLanguage(normalizedDealServiceLanguage));

    if (userId) {
      void updateUserPreferredLanguage({
        userId,
        preferredLanguage: toUserDbPreferredLanguage(normalizedDealServiceLanguage),
      });
    }
  }, [
    dispatch,
    isAuthenticated,
    hasApplicationContext,
    dealServicePersonalInformation,
    isDealServiceError,
    hasPreferredLanguageOverride,
    userProfile?.preferredLanguage,
    userId,
    updateUserPreferredLanguage,
  ]);

  return {
    isAuthenticated,
    hasApplicationContext,
    dealApplicationId,
  };
}
